// Global Instructions Rule Applied!
// Field configs for the funds Terms tab. Keys use the funds__<column>
// convention so InputManager auto-PATCHes to PATCH /funds {id, <column>}.
// Percent columns store decimals (0.02) but edit/display as percentages -
// handled via value scaling + valueTransformer.

import { FieldConfig, FieldTypeEnum, SelectTypeEnum } from '../../../core/components/InputManager/types.ts'
import { FundTermsRecord, FundTermsRecordFields } from '../../../state_manager/data_models/fund_terms_record.ts'
import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { fundStatusTag } from '../../global_configs/fundStatusDisplay.tsx'
import { LookupHelpEntry, lookupOptionsHelp } from './lookupOptionsHelp.tsx'
import React from 'react'
import dayjs from 'dayjs'

export interface FundTermsFieldOptions {
  timeUnitOptions: SelectListEntry[]
  anchorOptions: SelectListEntry[]
  accountOptions: SelectListEntry[]
  gpCommitmentBasisOptions: SelectListEntry[]
  feeBasisOptions: SelectListEntry[]
  frequencyOptions: SelectListEntry[]
  dayCountOptions: SelectListEntry[]
  lateCloserPolicyOptions: SelectListEntry[]
  feeBreakTreatmentOptions: SelectListEntry[]
  /** cfg.fee_break_treatments label + description, for the field's help "?" */
  feeBreakTreatmentHelp: LookupHelpEntry[]
  waivedFeeSettlementOptions: SelectListEntry[]
  /** cfg.waived_fee_settlements label + description, for the field's help "?" */
  waivedFeeSettlementHelp: LookupHelpEntry[]
  feePeriodSwitchTimingOptions: SelectListEntry[]
  pipelineOptions: SelectListEntry[]
  fundraisingStatusOptions: SelectListEntry[]
  investmentStatusOptions: SelectListEntry[]
  currencyOptions: SelectListEntry[]
  currencySymbol?: string
}

/** funds.year_end is the year-end MONTH (1-12) — calendar months, not cfg. */
export const FUND_YEAR_END_MONTH_OPTIONS: SelectListEntry[] = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: dayjs().month(i).format('MMMM')
}))

const DATE_FORMAT = 'MM/DD/YYYY'

function dateField(key: string, label: string, value: unknown): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.DATE,
    value: (value as string) ?? null,
    format: DATE_FORMAT,
    editable: true,
    allowClear: true
  }
}

function textField(key: string, label: string, value: unknown, extra: Partial<FieldConfig> = {}): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.TEXT,
    value: (value as string) ?? null,
    editable: true,
    ...extra
  }
}

function selectField(key: string, label: string, value: unknown, options: SelectListEntry[]): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.SELECT,
    value: (value as number) ?? null,
    options,
    selectType: SelectTypeEnum.FILTER,
    allowClear: true,
    editable: true
  }
}

function numberField(key: string, label: string, value: unknown): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.NUMBER,
    value: (value as number) ?? null,
    editable: true
  }
}

/** A multiple of contributed capital (1 = return of capital), displayed with an "x". */
function multipleField(key: string, label: string, value: unknown): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.NUMBER,
    value: (value as number) ?? null,
    displayValue: value == null ? undefined : `${value}x`,
    min: 0,
    editable: true
  }
}

function currencyField(key: string, label: string, value: unknown, currencySymbol?: string): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.CURRENCY,
    value: (value as number) ?? null,
    currencySymbol,
    editable: true
  }
}

/**
 * Read-only computed rows (the prog__* values view__fund_terms resolves
 * server-side). They render through the same InputManager as the editable
 * terms so they share its label column, font and row spacing — a hand-rolled
 * "Label: value" row sits at its own indent and in its own type.
 */
function computedDateField(key: string, label: string, value: unknown): FieldConfig {
  return { ...dateField(key, label, value), editable: false }
}

function computedCurrencyField(
  key: string,
  label: string,
  value: unknown,
  currencySymbol?: string,
  labelTooltip?: React.ReactNode
): FieldConfig {
  return { ...currencyField(key, label, value, currencySymbol), editable: false, labelTooltip }
}

function switchField(key: string, label: string, value: unknown): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.SWITCH,
    value: (value as boolean) ?? false,
    editable: true
  }
}

/** Percent column stored as a decimal (0.02): edit as 2, save as 0.02. */
function percentField(key: string, label: string, decimalValue: unknown, columnName: string): FieldConfig {
  const pct = decimalValue == null ? null : Number((Number(decimalValue) * 100).toFixed(6))
  return {
    key,
    label,
    type: FieldTypeEnum.NUMBER,
    value: pct,
    displayValue: pct == null ? undefined : `${pct}%`,
    editable: true,
    valueTransformer: (value: any) => ({
      [columnName]: value == null || value === '' ? null : Number(value) / 100
    })
  }
}

/**
 * Fund identity (Details section, 2026-09-09). The one field set shared by
 * the Terms tab (auto-patch, per field) and the New Fund form (edit mode,
 * one POST) — `terms` is Partial so the create form can pass its draft.
 */
export function getDetailsFields(terms: Partial<FundTermsRecord>, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    textField(FundTermsRecordFields.funds__name, 'Name', terms.funds__name, { required: true }),
    textField(FundTermsRecordFields.funds__legal_name, 'Legal Name', terms.funds__legal_name),
    selectField(
      FundTermsRecordFields.funds__functional_currency_id,
      'Functional Currency',
      terms.funds__functional_currency_id,
      options.currencyOptions
    ),
    selectField(FundTermsRecordFields.funds__year_end, 'Year End', terms.funds__year_end, FUND_YEAR_END_MONTH_OPTIONS),
    // funds.managed_by_us — funds we manage. Their managers constitute "us"
    // for deal-round lead flags; Bulk Add Prospects and the kanban only
    // offer these funds.
    switchField(FundTermsRecordFields.funds__managed_by_us, 'Managed By Us', terms.funds__managed_by_us ?? true),
    // funds.is_sbic — an SBA Small Business Investment Company fund. Companies
    // an SBIC fund invests in carry a Critical Technology Area, so the deal
    // commit gate asks for one when a commitment points here. Defaults FALSE:
    // most funds are not SBIC, and nothing anywhere matches a fund by name.
    switchField(FundTermsRecordFields.funds__is_sbic, 'SBIC Fund', terms.funds__is_sbic ?? false)
  ]
}

function textareaField(key: string, label: string, value: unknown, rows = 2): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.TEXTAREA,
    value: (value as string) ?? null,
    editable: true,
    rows,
    autoSize: { minRows: rows, maxRows: 6 }
  }
}

/**
 * The fund as a legal entity: its general partner and its address. Printed
 * on the fund's capital call notices (signature block and letterhead).
 */
export function getLegalEntityFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    textField(FundTermsRecordFields.funds__general_partner_name, 'General Partner', terms.funds__general_partner_name, {
      labelTooltip:
        'The general partner\'s legal name. The signature block of a capital call notice reads "<fund legal name> / By: <general partner>, its General Partner".'
    }),
    {
      ...textareaField(FundTermsRecordFields.funds__address, 'Address', terms.funds__address),
      labelTooltip: 'One line per row, as it prints in the letterhead of the fund\'s notices.'
    }
  ]
}

/**
 * What the LPA says about the fund's capital calls. A notice cites the
 * section the call is made under, and the call form checks the due date
 * against the notice period.
 */
export function getCapitalCallTermsFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    textField(FundTermsRecordFields.funds__lpa_agreement_name, 'Agreement Name', terms.funds__lpa_agreement_name, {
      placeholder: 'Amended and Restated Limited Partnership Agreement',
      labelTooltip: 'How a notice cites the fund\'s agreement.'
    }),
    textField(
      FundTermsRecordFields.funds__lpa_initial_contribution_clause,
      'Initial Contribution Section',
      terms.funds__lpa_initial_contribution_clause,
      {
        placeholder: '3.1(c)',
        labelTooltip:
          'The LPA section an investor\'s FIRST capital contribution is called under. The notice of an investor with nothing called before cites this section.'
      }
    ),
    textField(FundTermsRecordFields.funds__lpa_drawdown_clause, 'Drawdown Section', terms.funds__lpa_drawdown_clause, {
      placeholder: '3.2(a)',
      labelTooltip:
        'The LPA section every later capital contribution is called under. The notice of an investor with capital already called cites this section.'
    }),
    textField(FundTermsRecordFields.funds__lpa_expense_clause, 'Expense Contribution Section', terms.funds__lpa_expense_clause, {
      placeholder: '3.2(b)',
      labelTooltip:
        'Only where the LPA calls expenses on top of the commitment: the section that says so. Available to the notice wording as {lpa_expense_clause}.'
    }),
    {
      ...numberField(
        FundTermsRecordFields.funds__call_notice_days_initial,
        'Notice Period - Initial (days)',
        terms.funds__call_notice_days_initial
      ),
      labelTooltip:
        'The minimum notice the LPA requires for an initial contribution. The call form warns when the due date is sooner. Empty = not checked.'
    },
    {
      ...numberField(
        FundTermsRecordFields.funds__call_notice_days_drawdown,
        'Notice Period - Drawdown (days)',
        terms.funds__call_notice_days_drawdown
      ),
      labelTooltip:
        'The minimum notice the LPA requires for a drawdown. The call form warns when the due date is sooner. Empty = not checked.'
    },
    {
      ...switchField(
        FundTermsRecordFields.funds__call_notice_business_days,
        'Notice In Business Days',
        terms.funds__call_notice_business_days ?? false
      ),
      labelTooltip:
        'On: the notice periods count business days (Monday to Friday; public holidays are not known to the system and count). Off: calendar days.'
    }
  ]
}

/**
 * Who signs the fund's capital call notices and answers questions about
 * them: the signature block and the contact line of each notice.
 */
export function getNoticeSenderFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    textField(FundTermsRecordFields.funds__call_notice_sender_name, 'Notice Sender', terms.funds__call_notice_sender_name, {
      labelTooltip:
        'Who signs the fund\'s capital call notices and answers questions about them. Printed in the signature block and the contact line.'
    }),
    textField(FundTermsRecordFields.funds__call_notice_sender_title, 'Sender Title', terms.funds__call_notice_sender_title),
    textField(FundTermsRecordFields.funds__call_notice_sender_phone, 'Sender Phone', terms.funds__call_notice_sender_phone),
    textField(FundTermsRecordFields.funds__call_notice_sender_email, 'Sender Email', terms.funds__call_notice_sender_email)
  ]
}

export function getKeyDatesFields(terms: Partial<FundTermsRecord>): FieldConfig[] {
  return [
    dateField(FundTermsRecordFields.funds__formation_date, 'Formation Date', terms.funds__formation_date),
    dateField(FundTermsRecordFields.funds__initial_closing_date, 'Initial Closing Date', terms.funds__initial_closing_date),
    dateField(
      FundTermsRecordFields.funds__initial_contribution_date,
      'Initial Contribution Date',
      terms.funds__initial_contribution_date
    )
  ]
}

export function getFinalClosingFields(terms: FundTermsRecord, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    numberField(FundTermsRecordFields.funds__final_closing_term, 'Term', terms.funds__final_closing_term),
    selectField(
      FundTermsRecordFields.funds__final_closing_term_units_id,
      'Term Units',
      terms.funds__final_closing_term_units_id,
      options.timeUnitOptions
    ),
    selectField(
      FundTermsRecordFields.funds__final_closing_anchor_id,
      'Anchor',
      terms.funds__final_closing_anchor_id,
      options.anchorOptions
    ),
    dateField(
      FundTermsRecordFields.funds__final_closing_anchor_date,
      'Custom Anchor Date',
      terms.funds__final_closing_anchor_date
    ),
    dateField(
      FundTermsRecordFields.funds__final_closing_date_override,
      'Date Override',
      terms.funds__final_closing_date_override
    ),
    dateField(
      FundTermsRecordFields.funds__extended_final_closing_date,
      'Extended Date',
      terms.funds__extended_final_closing_date
    )
  ]
}

export function getInvestmentPeriodFields(terms: FundTermsRecord, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    numberField(FundTermsRecordFields.funds__investment_period_term, 'Term', terms.funds__investment_period_term),
    selectField(
      FundTermsRecordFields.funds__investment_period_term_units_id,
      'Term Units',
      terms.funds__investment_period_term_units_id,
      options.timeUnitOptions
    ),
    selectField(
      FundTermsRecordFields.funds__investment_period_anchor_id,
      'Anchor',
      terms.funds__investment_period_anchor_id,
      options.anchorOptions
    ),
    dateField(
      FundTermsRecordFields.funds__investment_period_anchor_date,
      'Custom Anchor Date',
      terms.funds__investment_period_anchor_date
    ),
    dateField(
      FundTermsRecordFields.funds__investment_period_date_override,
      'End Date Override',
      terms.funds__investment_period_date_override
    ),
    dateField(
      FundTermsRecordFields.funds__extended_investment_period_end_date,
      'Extended End Date',
      terms.funds__extended_investment_period_end_date
    )
  ]
}

export function getFundTermFields(terms: FundTermsRecord, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    numberField(FundTermsRecordFields.funds__base_term, 'Term', terms.funds__base_term),
    selectField(
      FundTermsRecordFields.funds__base_term_units_id,
      'Term Units',
      terms.funds__base_term_units_id,
      options.timeUnitOptions
    ),
    selectField(
      FundTermsRecordFields.funds__base_term_anchor_id,
      'Anchor',
      terms.funds__base_term_anchor_id,
      options.anchorOptions
    ),
    dateField(
      FundTermsRecordFields.funds__base_term_anchor_date,
      'Custom Anchor Date',
      terms.funds__base_term_anchor_date
    ),
    dateField(FundTermsRecordFields.funds__termination_date, 'Termination Date Override', terms.funds__termination_date)
  ]
}

export function getFinalClosingComputedFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    computedDateField(
      FundTermsRecordFields.prog__effective_final_closing_date,
      'Effective Final Closing',
      terms.prog__effective_final_closing_date
    )
  ]
}

export function getInvestmentPeriodComputedFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    computedDateField(
      FundTermsRecordFields.prog__effective_investment_period_start,
      'Effective Start',
      terms.prog__effective_investment_period_start
    ),
    computedDateField(
      FundTermsRecordFields.prog__effective_investment_period_end,
      'Effective End',
      terms.prog__effective_investment_period_end
    )
  ]
}

export function getFundTermComputedFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    computedDateField(
      FundTermsRecordFields.prog__effective_termination_date,
      'Effective Termination',
      terms.prog__effective_termination_date
    )
  ]
}

/**
 * Effective fee-schedule start and end (the last day the fee periods cover —
 * no fee accrues after it), plus — on an SBIC fund — Regulatory Capital and
 * Assumed Leverage as at today (13 CFR 107.230, fund__regulatory_capital_at).
 */
export function getManagementFeeComputedFields(terms: FundTermsRecord, options: FundTermsFieldOptions): FieldConfig[] {
  const fields: FieldConfig[] = [
    computedDateField(
      FundTermsRecordFields.prog__effective_fee_schedule_start,
      'Effective Fee Schedule Start',
      terms.prog__effective_fee_schedule_start
    ),
    computedDateField(
      FundTermsRecordFields.prog__effective_fee_schedule_end,
      'Effective Fee Schedule End',
      terms.prog__effective_fee_schedule_end
    ),
    // resolved start of fee capital tracking (blank = not tracked: switched
    // off, or no issued capital call yet)
    computedDateField(
      FundTermsRecordFields.prog__fee_capital_tracking_start,
      'Fee Capital Tracked Since',
      terms.prog__fee_capital_tracking_start
    )
  ]
  if (terms.funds__is_sbic) {
    fields.push(
      computedCurrencyField(
        FundTermsRecordFields.prog__regulatory_capital,
        'Regulatory Capital (today)',
        terms.prog__regulatory_capital,
        options.currencySymbol
      ),
      computedCurrencyField(
        FundTermsRecordFields.prog__assumed_leverage,
        'Assumed Leverage (today)',
        terms.prog__assumed_leverage,
        options.currencySymbol,
        terms.funds__sbic_gearing_ratio != null
          ? `Regulatory Capital × ${terms.funds__sbic_gearing_ratio} (the SBIC gearing ratio).`
          : 'Set the SBIC gearing ratio to compute this.'
      )
    )
  }
  return fields
}

export function getCommitmentFields(terms: FundTermsRecord, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    currencyField(
      FundTermsRecordFields.funds__max_commitments,
      'Max Commitments',
      terms.funds__max_commitments,
      options.currencySymbol
    ),
    switchField(FundTermsRecordFields.funds__max_includes_gp, 'Max Includes GP', terms.funds__max_includes_gp),
    percentField(
      FundTermsRecordFields.funds__min_gp_commit_perc,
      'Min GP Commitment %',
      terms.funds__min_gp_commit_perc,
      'min_gp_commit_perc'
    ),
    selectField(
      FundTermsRecordFields.funds__min_gp_commit_base_id,
      'Min GP Commitment Basis',
      terms.funds__min_gp_commit_base_id,
      options.gpCommitmentBasisOptions
    ),
    currencyField(
      FundTermsRecordFields.funds__min_gp_commit_amount,
      'Min GP Commitment Amount',
      terms.funds__min_gp_commit_amount,
      options.currencySymbol
    ),
    // Whether the commitment covers the expenses / the management fees
    // (spec A3.9 addendum part 4). Unset counts as included everywhere -
    // calls, % Drawn, Uncalled - so an unset term shows On, which is what
    // applies. Off: those lines are called ON TOP of the commitment.
    {
      ...switchField(
        FundTermsRecordFields.funds__commitment_includes_expenses,
        'Commitment Includes Expenses',
        terms.funds__commitment_includes_expenses ?? true
      ),
      labelTooltip:
        "On (the default): expenses called from investors draw down their commitments. Off: expenses are called on top of the commitment - they do not count toward % Drawn or Uncalled, and a call sized as a % of committed adds them on top of the %. Call them on the 'expenses' line item: the combined investments-and-expenses item cannot be split."
    },
    {
      ...switchField(
        FundTermsRecordFields.funds__commitment_includes_mgmt_fees,
        'Commitment Includes Mgmt Fees',
        terms.funds__commitment_includes_mgmt_fees ?? true
      ),
      labelTooltip:
        'On (the default): management fees called from investors draw down their commitments. Off: fees are called on top of the commitment - they do not count toward % Drawn or Uncalled, and a call sized as a % of committed adds each investor\'s fee on top of its share.'
    }
  ]
}

export function getManagerFields(terms: Partial<FundTermsRecord>, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    // funds.managed_by — the fund manager entity (accounts row). Managers of
    // managed_by_us funds constitute "us": deal-round lead flags point our
    // round_participations rows at these accounts.
    selectField(
      FundTermsRecordFields.funds__managed_by,
      'Fund Manager',
      terms.funds__managed_by,
      options.accountOptions
    )
  ]
}

export function getFundraisingFields(terms: Partial<FundTermsRecord>, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    // funds.pipeline_origination — the fund's Fundraising origination
    // pipeline; drives stage lists + default stage everywhere prospects for
    // this fund are created (drawer, kanban, Bulk Add Prospects). Empty falls
    // back to the default Fundraising pipeline.
    selectField(
      FundTermsRecordFields.funds__pipeline_origination,
      'Fundraising Pipeline',
      terms.funds__pipeline_origination,
      options.pipelineOptions
    )
  ]
}

/**
 * Carried Interest section: the fund's carry terms as the LPA states them,
 * the default every commitment without a side-letter term inherits (the
 * engine's wf__lp_terms resolves commitment → fund → 0 % / 1x). The labels
 * are the commitment Terms section's, so the two read alike. The tiers of the
 * Waterfall section below reference these figures by name ("the commitment
 * base carry rate") and print the default beside each.
 */
/**
 * Partners' share of fund results (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md
 * 4.5): how the fund shares its unstamped income, expenses, distributions and
 * carry between the partners. READ-ONLY until a fund converts (decision 12):
 * PR 4's scripted conversion books the closed years as filed and sets the
 * basis; flipping it here would double-count a fund's hand corrections.
 */
export function getAllocationBasisFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    textField(FundTermsRecordFields.prog__pnl_allocation_basis_label, 'Allocation basis', terms.prog__pnl_allocation_basis_label ?? 'Static', {
      editable: false,
      labelTooltip:
        'Static: the commitments Closed today share every result, whatever its date. Financial year: each year\'s results go to the partners in by its end, booked per partner and GL account at the year close, with a later closer caught up from the following years\' own items per K-1 tax category. Set by the conversion of a fund, not here.'
    })
  ]
}

export function getCarriedInterestFields(terms: FundTermsRecord): FieldConfig[] {
  return [
    {
      ...percentField(FundTermsRecordFields.funds__base_carry_rate, 'Carry (Base) %', terms.funds__base_carry_rate, 'base_carry_rate'),
      labelTooltip:
        "The carried interest rate once capital is returned at the base hurdle - the LPA's rate. A commitment with a side letter sets its own on the Commitment form; every other commitment inherits this one."
    },
    {
      ...multipleField(FundTermsRecordFields.funds__base_hurdle_multiple, 'Hurdle (Base) x', terms.funds__base_hurdle_multiple),
      labelTooltip: 'The multiple of contributed capital the LPs receive before the carry starts: 1x is a plain return of capital.'
    },
    {
      ...percentField(
        FundTermsRecordFields.funds__stepped_carry_rate,
        'Carry (Stepped) %',
        terms.funds__stepped_carry_rate,
        'stepped_carry_rate'
      ),
      labelTooltip: 'The carried interest rate once the stepped hurdle is met (Origin: 25% after 3x). Blank = no step, the base rate throughout.'
    },
    {
      ...multipleField(
        FundTermsRecordFields.funds__stepped_hurdle_multiple,
        'Hurdle (Stepped) x',
        terms.funds__stepped_hurdle_multiple
      ),
      labelTooltip: 'The multiple of contributed capital at which the stepped rate takes over (the performance distribution milestone). Blank = no step.'
    }
  ]
}

export function getManagementFeeFields(terms: FundTermsRecord, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    // Headline (reference) rate: the fallback when no fee periods exist and
    // the base against which per-commitment side-letter rates scale.
    percentField(
      FundTermsRecordFields.funds__mgmt_fee_rate,
      'Headline Fee Rate',
      terms.funds__mgmt_fee_rate,
      'mgmt_fee_rate'
    ),
    selectField(
      FundTermsRecordFields.funds__mgmt_fee_basis_id,
      'Headline Fee Basis',
      terms.funds__mgmt_fee_basis_id,
      options.feeBasisOptions
    ),
    // SBIC funds only: Assumed Leverage = Regulatory Capital × this ratio
    // (the 'Regulatory capital + assumed leverage' fee basis and the
    // § 11.9(b) cross-credit read it). Regulatory Capital itself is
    // computed (fund__regulatory_capital_at) and shown read-only on the tab.
    // Replaces the retired static funds.mgmt_fee_assumed_leverage input.
    ...(terms.funds__is_sbic
      ? [
          numberField(FundTermsRecordFields.funds__sbic_gearing_ratio, 'SBIC Gearing Ratio (×)', terms.funds__sbic_gearing_ratio),
          // the distribution engine's twelve-months-of-licensure gate (SBIC
          // LPA §7.01(a)): a missing date fails the gate and says so
          dateField(FundTermsRecordFields.funds__sbic_license_date, 'SBIC Licence Date', terms.funds__sbic_license_date)
        ]
      : []),
    selectField(
      FundTermsRecordFields.funds__fee_schedule_anchor_id,
      'Fee Schedule Anchor',
      terms.funds__fee_schedule_anchor_id,
      options.anchorOptions
    ),
    dateField(
      FundTermsRecordFields.funds__fee_schedule_anchor_date,
      'Fee Schedule Custom Date',
      terms.funds__fee_schedule_anchor_date
    ),
    selectField(
      FundTermsRecordFields.funds__mgmt_fee_frequency_id,
      'Frequency',
      terms.funds__mgmt_fee_frequency_id,
      options.frequencyOptions
    ),
    selectField(
      FundTermsRecordFields.funds__mgmt_fee_day_count_id,
      'Day Count',
      terms.funds__mgmt_fee_day_count_id,
      options.dayCountOptions
    ),
    // Fee period switch: fee periods run consecutively from the schedule
    // anchor, so one usually ends mid-quarter. Blank / 'On the switch date'
    // bills that quarter in two parts; 'Start of the next billing period'
    // lets the ending period run to the quarter end (the Fee Periods table
    // shows each period's resulting "Applies From" date).
    {
      ...selectField(
        FundTermsRecordFields.funds__fee_period_switch_timing_id,
        'Fee Period Switch',
        terms.funds__fee_period_switch_timing_id,
        options.feePeriodSwitchTimingOptions
      ),
      labelTooltip:
        'When a fee period ends inside a billing period (e.g. mid-quarter). Blank or "On the switch date": the next period\'s rate and basis apply from that day and the quarter is billed in two parts. "Start of the next billing period": the ending period runs to the end of the quarter and the next one applies from the following quarter start. Needs a Monthly, Quarterly or Semi-annual frequency.'
    },
    // 'Full catch-up' bills late closers as if closed at the fee schedule
    // start and enables the Equalization section on the Capital Calls tab;
    // unset / 'From closing date' keeps the historical behaviour.
    selectField(
      FundTermsRecordFields.funds__late_closer_policy_id,
      'Late Closer Policy',
      terms.funds__late_closer_policy_id,
      options.lateCloserPolicyOptions
    ),
    // Fee break treatment (spec A3.9): whether a side-letter fee break
    // reduces the LP's capital calls (blank = Contribute less). A
    // consolidated fund follows its PRIMARY fund's setting, so on another
    // member the field is read-only, shows the treatment that applies and
    // names the primary. The help "?" lists the options with their cfg
    // descriptions (addendum part 1).
    terms.prog__fee_break_treatment_primary_fund
      ? {
          ...selectField(
            FundTermsRecordFields.funds__fee_break_treatment_id,
            'Fee Break Treatment',
            terms.prog__fee_break_treatment_effective_id,
            options.feeBreakTreatmentOptions
          ),
          editable: false,
          labelTooltip: lookupOptionsHelp(
            `Set on the consolidated fund's primary fund, ${terms.prog__fee_break_treatment_primary_fund}: ${
              terms.prog__fee_break_in_full ? 'investors contribute in full' : 'investors contribute less'
            }.`,
            options.feeBreakTreatmentHelp
          )
        }
      : {
          ...selectField(
            FundTermsRecordFields.funds__fee_break_treatment_id,
            'Fee Break Treatment',
            terms.funds__fee_break_treatment_id,
            options.feeBreakTreatmentOptions
          ),
          labelTooltip: lookupOptionsHelp(
            "How a capital call treats an investor's side-letter fee break. Blank = the contribute-less option.",
            options.feeBreakTreatmentHelp
          )
        },
    // Waived fee settlement (spec A3.9 addendum part 1; replaces the Waived
    // Fees Repaid switch): what the fund owes its reduced-fee partners for
    // their waived fees. The fund's own LPA term - a consolidated fund's
    // members are set one by one. An owed setting needs investors to
    // contribute in full (the effective treatment above; the funds trigger
    // refuses it otherwise), so the select is read-only under 'Contribute
    // less' and the help says why.
    {
      ...selectField(
        FundTermsRecordFields.funds__waived_fee_settlement_id,
        'Waived Fee Settlement',
        terms.funds__waived_fee_settlement_id,
        options.waivedFeeSettlementOptions
      ),
      editable: Boolean(terms.prog__fee_break_in_full),
      labelTooltip: lookupOptionsHelp(
        terms.prog__fee_break_in_full
          ? "How this fund settles its reduced-fee investors' waived fees - a term of the fund's own LPA (set on each member of a consolidated fund). Blank = nothing owed."
          : `Not available while investors contribute less: a reduced-fee investor already paid its fee break less, so nothing is owed. Set the Fee Break Treatment${
              terms.prog__fee_break_treatment_primary_fund
                ? ` of the primary fund, ${terms.prog__fee_break_treatment_primary_fund},`
                : ''
            } to a contribute-in-full option first.`,
        options.waivedFeeSettlementHelp
      )
    },
    // Side-letter rates on assumed leverage (spec A3.9 addendum part 3):
    // on the 'Regulatory capital + assumed leverage' basis the fee on an
    // LP's assumed leverage is charged at the full rate whatever its side
    // letter. Shown only when the headline basis or a fee period uses that
    // basis - it means nothing on any other.
    ...(terms.prog__has_regulatory_capital_basis
      ? [
          {
            ...switchField(
              FundTermsRecordFields.funds__side_letter_rate_on_leverage,
              'Side-letter Rates Apply to Assumed Leverage',
              terms.funds__side_letter_rate_on_leverage ?? false
            ),
            labelTooltip:
              "Off (the default): on the 'Regulatory capital + assumed leverage' fee basis the fee on an investor's assumed leverage is charged at the full rate whatever its side letter; a reduced or waived rate applies to its Regulatory Capital share only. On: side-letter rates also apply to the fee on assumed leverage."
          }
        ]
      : []),
    // Fee capital tracking (spec A3.8): each LP's fees paid are measured
    // against the fee capital it contributed; what that does not cover is
    // recorded as paid from partnership assets. On by default; the date is
    // optional (blank = from the first issued capital call).
    switchField(
      FundTermsRecordFields.funds__fee_capital_tracking,
      'Track Fee Capital',
      terms.funds__fee_capital_tracking ?? true
    ),
    dateField(
      FundTermsRecordFields.funds__fee_capital_tracked_from,
      'Fee Capital Tracked From',
      terms.funds__fee_capital_tracked_from
    )
  ]
}

/**
 * Fund lifecycle status (fund status, 2026-09-04).
 *
 * `terms` is Partial: the New Fund form passes its draft (no `__name` /
 * `__is_*` flags, so no Tag — the Tag only renders in read mode anyway).
 *
 * Its OWN section, first on the tab — not folded into 'Fundraising', which is
 * about HOW we raise (which pipeline). The investing clock has no business in
 * that section, and the two clocks belong together.
 *
 * Both fields are plain selects over cfg.fund_statuses. Nothing is coerced: a
 * NULL status stays NULL and shows no Tag, because NULL means "not yet
 * classified" and every consumer is fail-open on it.
 *
 * The status is HUMAN-OWNED. The dates never write it — `prog__*_drift` is
 * surfaced by TermsTab as a hint next to the field, and even that is suppressed
 * server-side on a cancelled fund.
 */
export function getStatusFields(terms: Partial<FundTermsRecord>, options: FundTermsFieldOptions): FieldConfig[] {
  return [
    {
      ...selectField(
        FundTermsRecordFields.funds__fundraising_status_id,
        'Fundraising Status',
        terms.funds__fundraising_status_id,
        options.fundraisingStatusOptions
      ),
      displayValue: fundStatusTag(terms.funds__fundraising_status__name, {
        is_open: terms.funds__fundraising_status__is_open,
        is_terminal: terms.funds__fundraising_status__is_terminal
      })
    },
    {
      ...selectField(
        FundTermsRecordFields.funds__investment_status_id,
        'Investment Status',
        terms.funds__investment_status_id,
        options.investmentStatusOptions
      ),
      displayValue: fundStatusTag(terms.funds__investment_status__name, {
        is_open: terms.funds__investment_status__is_open,
        is_terminal: terms.funds__investment_status__is_terminal
      })
    }
  ]
}
